// The permanent InteractAds Tag of a Distribution (7.6H, ADR-0028): its public
// identity, the pasteable universal web tag built by the server (the browser
// never builds or edits it) and an honest verification label. No delivery
// hostname is invented: without a configured delivery origin there is no tag.
import { Tag as TagIcon } from "lucide-react";
import type { Tag } from "@/api/types";
import { displayFormat } from "@/lib/labels";
import { CopyButton } from "@/ui/copy-button";
import { Callout, Panel } from "@/ui/layout";

const VERIFICATION_LABEL: Record<string, string> = {
  VERIFIED: "verificado por InteractAds para este producto",
  LIMITED: "compatible con limitaciones",
  UNVERIFIED: "sin verificar por InteractAds",
};

const UNAVAILABLE: Record<string, string> = {
  UNSUPPORTED: "Esta plataforma no aceptó este producto o formato en nuestras pruebas: no generamos código para ella.",
  ENVIRONMENT_NOT_AVAILABLE: "Este entorno de ejecución todavía no está disponible.",
  DELIVERY_ORIGIN_NOT_CONFIGURED:
    "El código para pegar estará disponible cuando se habilite el servicio de entrega de InteractAds.",
};

export function TagCard({ tag, serving, platformName }: { tag: Tag; serving: boolean; platformName: string }) {
  const descriptor = JSON.stringify(tag.bootstrap, null, 2);
  return (
    <Panel className="grid gap-4 p-4">
      <div className="flex items-start gap-3">
        <TagIcon className="mt-0.5 size-5 text-brand" aria-hidden="true" />
        <div className="grid gap-1">
          <h3 className="text-sm font-semibold">Tag permanente</h3>
          <p className="text-sm text-muted">
            Este identificador no cambia aunque publiques nuevas versiones, vuelvas a una anterior o pauses la
            distribución.
          </p>
        </div>
      </div>
      <div className="grid gap-1">
        <span className="text-xs text-muted">ID público del Tag</span>
        <div className="flex flex-wrap items-center gap-2">
          <code
            className="rounded-sm border border-line bg-sunken px-2 py-1 font-mono text-sm"
            data-testid="tag-public-id"
          >
            {tag.publicId}
          </code>
          <CopyButton value={tag.publicId} label="Copiar ID público del Tag" />
        </div>
      </div>
      <div className="grid gap-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted">
            Descriptor del Tag · {platformName} · {displayFormat(tag.bootstrap.format)}
          </span>
          <CopyButton value={descriptor} label="Copiar descriptor del Tag" />
        </div>
        <pre className="overflow-x-auto rounded-sm border border-line bg-sunken p-2 font-mono text-xs">
          {descriptor}
        </pre>
      </div>
      {tag.web ? (
        <div className="grid gap-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted">
              Código para pegar en tu plataforma · {tag.web.adapter.label} · {VERIFICATION_LABEL[tag.web.verification]}
            </span>
            <CopyButton value={tag.web.html} label="Copiar código del Tag" />
          </div>
          <pre
            className="overflow-x-auto rounded-sm border border-line bg-sunken p-2 font-mono text-xs whitespace-pre-wrap break-all"
            data-testid="tag-web-html"
          >
            {tag.web.html}
          </pre>
        </div>
      ) : null}
      <Callout title={serving ? "Cómo usarás este Tag" : "Este Tag aún no está activo"}>
        {tag.web
          ? "Pega este código una sola vez en tu plataforma: cuando publiques otra versión o vuelvas a una anterior, el mismo código servirá la versión activa. "
          : (UNAVAILABLE[tag.webUnavailable ?? "DELIVERY_ORIGIN_NOT_CONFIGURED"] ?? "") + " "}
        La compatibilidad depende de cada plataforma, producto y formato: revisa el resumen de compatibilidad de esta
        distribución.
      </Callout>
    </Panel>
  );
}
